/**
 * Lijndiagram van het prijsverloop (design-handoff, scherm 5 en scherm Prijsgrafiek).
 *
 * Bewust een eigen SVG en geen grafiekbibliotheek: er is één diagramvorm nodig, de lijnkleuren
 * liggen vast in de huisstijl, en het scheelt een forse afhankelijkheid in de frontend.
 *
 * Een lege meting onderbreekt de lijn in plaats van hem door te trekken — anders zou een gat in
 * de gegevens eruitzien als een vlakke prijs.
 *
 * Twee maten, één implementatie. Het productdetail heeft een klein diagram naast de tabel
 * (`compact`); het scherm Prijsgrafiek heeft het diagram als hoofdzaak (`volledig`, met meer
 * hulplijnen, meer datumlabels en een punt op de laatste meting). Dat als twee componenten bouwen
 * lag voor de hand, maar dan bestaat "hoe wordt een prijsreeks een lijn" twee keer — en dat is
 * precies hoe de e-mailopmaak uit elkaar is gelopen.
 */

import {
  afwijkingTovEigen,
  formatEuro,
  formatDayLabel,
  formatPercent,
  laatsteWaarde,
} from '@prijsmonitor/shared';

import styles from './PriceChart.module.css';

export interface ChartSeries {
  sourceId: string;
  name: string;
  isOwn: boolean;
  points: Array<{ dateKey: string; priceCents: number | null }>;
}

/**
 * Kleuren per bron, gekozen op maximale onderlinge afstand (zie `tokens.css`).
 *
 * Voorbij tien bronnen is de tintruimte op. Dan begint de reeks opnieuw, maar gestreept — een
 * gestreepte lijn is onmiskenbaar een andere reeks dan een doorlopende in dezelfde kleur, en dat is
 * eerlijker dan een elfde tint verzinnen die op een van de tien lijkt.
 */
const KLEUREN = [
  'var(--c-bron-1)',
  'var(--c-bron-2)',
  'var(--c-bron-3)',
  'var(--c-bron-4)',
  'var(--c-bron-5)',
  'var(--c-bron-6)',
  'var(--c-bron-7)',
  'var(--c-bron-8)',
  'var(--c-bron-9)',
  'var(--c-bron-10)',
];

const STREEP = '7 4';

/** Hoogste aantal tekens in een naamlabel naast een lijn; de volledige naam blijft in de titel. */
const LABEL_TEKENS = 20;

interface Formaat {
  breedte: number;
  hoogte: number;
  marge: { top: number; right: number; bottom: number; left: number };
  /** Aantal horizontale hulplijnen met een waarde ernaast. */
  hulplijnen: number;
  /** Hoogste aantal datumlabels onder de as; er wordt gelijkmatig uit gekozen. */
  datumlabels: number;
  /** Een punt op de laatste meting van elke lijn. */
  eindpunt: boolean;
  /** De bronnaam naast dat punt. Vraagt ruimte rechts, dus alleen waar het diagram de hoofdzaak is. */
  eindlabel: boolean;
}

const FORMATEN: Record<'compact' | 'volledig', Formaat> = {
  compact: {
    breedte: 760,
    hoogte: 250,
    marge: { top: 16, right: 16, bottom: 28, left: 62 },
    hulplijnen: 3,
    datumlabels: 3,
    eindpunt: false,
    eindlabel: false,
  },
  volledig: {
    /*
     * De rechtermarge is de ruimte voor de naamlabels. Die kost een vijfde van de breedte, en dat is
     * hem waard: met negen concurrenten was uit de kleur alleen niet meer op te maken welke lijn
     * welke bron was, en dan moet je van legenda naar lijn gaan puzzelen.
     */
    breedte: 1000,
    hoogte: 340,
    marge: { top: 18, right: 176, bottom: 46, left: 76 },
    hulplijnen: 5,
    datumlabels: 7,
    eindpunt: true,
    eindlabel: true,
  },
};

/**
 * Verticale plaatsen voor de labels, zodat ze elkaar niet overschrijven.
 *
 * Eindigen twee lijnen op bijna dezelfde hoogte — en dat gebeurt vaak, want concurrenten zitten
 * dicht bij elkaar — dan zouden de teksten over elkaar heen vallen. Dus: op hoogte sorteren en van
 * boven naar beneden uit elkaar duwen tot ze passen. Wordt de stapel te hoog voor het vlak, dan
 * schuift hij als geheel omhoog; dat is beter dan labels die onder het diagram uit lopen.
 */
function labelHoogtes(
  ruw: Array<{ sourceId: string; y: number }>,
  ruimte: { top: number; bottom: number },
  afstand: number,
): Map<string, number> {
  const gesorteerd = [...ruw].sort((a, b) => a.y - b.y);
  let vorige = -Infinity;
  for (const label of gesorteerd) {
    label.y = Math.max(label.y, vorige + afstand);
    vorige = label.y;
  }

  const laagste = gesorteerd.at(-1);
  if (laagste && laagste.y > ruimte.bottom) {
    const verschuiving = Math.min(laagste.y - ruimte.bottom, gesorteerd[0]!.y - ruimte.top);
    if (verschuiving > 0) for (const label of gesorteerd) label.y -= verschuiving;
  }

  return new Map(gesorteerd.map((label) => [label.sourceId, label.y]));
}

export function PriceChart({
  series,
  hidden,
  onToggle,
  variant = 'compact',
  modus = 'absoluut',
}: {
  series: ChartSeries[];
  hidden: Set<string>;
  onToggle: (sourceId: string) => void;
  variant?: 'compact' | 'volledig';
  /**
   * `relatief` zet elke bron af tegen de eigen prijs van diezelfde dag, in procenten. Onze eigen
   * lijn is dan de nullijn en verdwijnt uit het diagram — hij zou een rechte lijn op nul zijn.
   */
  modus?: 'absoluut' | 'relatief';
}): React.JSX.Element {
  const formaat = FORMATEN[variant];
  const relatief = modus === 'relatief';

  // Alle dagen die in enige reeks voorkomen, oplopend. Dat is de x-as.
  const dagen = [...new Set(series.flatMap((s) => s.points.map((p) => p.dateKey)))].sort();

  /** De prijzen van een reeks, uitgelijnd op `dagen`; null waar niet is gemeten. */
  function waardenVan(reeks: ChartSeries): Array<number | null> {
    const perDag = new Map(reeks.points.map((p) => [p.dateKey, p.priceCents]));
    return dagen.map((dag) => perDag.get(dag) ?? null);
  }

  const eigen = series.find((s) => s.isOwn);
  const eigenWaarden = eigen ? waardenVan(eigen) : dagen.map(() => null);

  /** Wat er van deze reeks in het diagram komt: de prijs, of de afwijking van de eigen prijs. */
  function getoondeWaarden(reeks: ChartSeries): Array<number | null> {
    const waarden = waardenVan(reeks);
    return relatief ? afwijkingTovEigen(waarden, eigenWaarden) : waarden;
  }

  const getekend = series.filter((s) => !hidden.has(s.sourceId) && !(relatief && s.isOwn));
  const waarden = getekend
    .flatMap((s) => getoondeWaarden(s))
    .filter((v): v is number => v !== null);

  if (dagen.length === 0 || waarden.length === 0) {
    return <p className={styles.empty}>Nog geen metingen om een verloop van te tonen.</p>;
  }

  let onder: number;
  let boven: number;
  if (relatief) {
    /*
     * Symmetrisch rond nul, want de nullijn is hier de betekenis: erboven is duurder dan wij,
     * eronder goedkoper. Een asymmetrische as zou die scheidslijn uit het midden trekken en het
     * beeld laten kantelen zonder dat er iets aan de cijfers verandert.
     */
    const uiterste = Math.max(...waarden.map(Math.abs), 2) * 1.15;
    onder = -uiterste;
    boven = uiterste;
  } else {
    const min = Math.min(...waarden);
    const max = Math.max(...waarden);
    // Een marge voorkomt dat lijnen tegen de rand plakken; bij één waarde een vaste marge.
    const spreiding = max - min || Math.max(max * 0.1, 100);
    onder = min - spreiding * 0.05;
    boven = max + spreiding * 0.05;
  }

  const vlakBreedte = formaat.breedte - formaat.marge.left - formaat.marge.right;
  const vlakHoogte = formaat.hoogte - formaat.marge.top - formaat.marge.bottom;

  const x = (dagIndex: number): number =>
    formaat.marge.left + (dagIndex / Math.max(dagen.length - 1, 1)) * vlakBreedte;
  const y = (waarde: number): number =>
    formaat.marge.top + vlakHoogte - ((waarde - onder) / (boven - onder)) * vlakHoogte;

  const formatteer = (waarde: number): string =>
    relatief ? formatPercent(waarde) : formatEuro(Math.round(waarde));

  /** Bouwt het pad en breekt af bij een lege meting. */
  function pad(reeksWaarden: Array<number | null>): string {
    let d = '';
    let open = false;
    reeksWaarden.forEach((waarde, index) => {
      if (waarde === null) {
        open = false;
        return;
      }
      d += `${open ? 'L' : 'M'}${x(index).toFixed(1)},${y(waarde).toFixed(1)} `;
      open = true;
    });
    return d.trim();
  }

  /**
   * Kleur en streeppatroon van een bron.
   *
   * De plaats in de volledige lijst bepaalt ze, niet de plaats in wat er nu getekend wordt: verberg
   * je een bron in de legenda, dan mogen de andere lijnen niet van kleur verspringen.
   */
  function stijlVan(reeks: ChartSeries): { kleur: string; streep?: string } {
    if (reeks.isOwn) return { kleur: 'var(--c-line-own)' };
    const index = series.filter((s) => !s.isOwn).indexOf(reeks);
    return {
      kleur: KLEUREN[index % KLEUREN.length] as string,
      ...(index >= KLEUREN.length ? { streep: STREEP } : {}),
    };
  }

  const hulplijnen = Array.from(
    { length: formaat.hulplijnen },
    (_, i) => onder + ((boven - onder) * i) / (formaat.hulplijnen - 1),
  );

  // Gelijkmatig gekozen datumlabels; de laatste dag staat er altijd bij, want dat is "nu".
  const stap = Math.max(1, Math.ceil(dagen.length / formaat.datumlabels));
  const labelIndexen = dagen
    .map((_, index) => index)
    .filter((index) => index % stap === 0 || index === dagen.length - 1);

  /*
   * De naamlabels bij de lijneinden. Het label staat naast het púnt waar de lijn ophoudt en niet
   * altijd tegen de rechterrand: een bron die halverwege is gestopt met meten hoort zijn naam daar te
   * hebben staan, niet op een plek waar hij geen lijn heeft.
   */
  const eindlabels = formaat.eindlabel
    ? (() => {
        const ruw = getekend.flatMap((reeks) => {
          const laatste = laatsteWaarde(getoondeWaarden(reeks));
          if (!laatste) return [];
          const { kleur } = stijlVan(reeks);
          const naam = (reeks.isOwn ? '★ ' : '') + reeks.name;
          return [
            {
              sourceId: reeks.sourceId,
              puntX: x(laatste.dag),
              puntY: y(laatste.waarde),
              y: y(laatste.waarde),
              kleur,
              volledig: naam,
              tekst:
                naam.length > LABEL_TEKENS ? `${naam.slice(0, LABEL_TEKENS - 1).trimEnd()}…` : naam,
            },
          ];
        });

        const hoogtes = labelHoogtes(
          ruw.map((l) => ({ sourceId: l.sourceId, y: l.y })),
          { top: formaat.marge.top + 4, bottom: formaat.hoogte - formaat.marge.bottom - 4 },
          13,
        );

        return ruw.map((label) => ({
          ...label,
          y: hoogtes.get(label.sourceId) ?? label.y,
          // Rechts van het punt, maar nooit zo ver dat het label buiten het vlak valt.
          x: Math.min(label.puntX + 9, formaat.breedte - formaat.marge.right + 9),
        }));
      })()
    : [];

  return (
    <div>
      <svg
        className={variant === 'volledig' ? styles.chartVol : styles.chart}
        viewBox={`0 0 ${formaat.breedte} ${formaat.hoogte}`}
        role="img"
        aria-label={
          relatief
            ? `Afwijking ten opzichte van onze eigen prijs voor ${getekend.map((s) => s.name).join(', ')}`
            : `Prijsverloop van ${getekend.map((s) => s.name).join(', ')}`
        }
      >
        {hulplijnen.map((waarde) => {
          // In procentweergave is de nullijn de scheidslijn duurder/goedkoper; die mag opvallen.
          const isNullijn = relatief && Math.abs(waarde) < (boven - onder) / 40;
          return (
            <g key={waarde}>
              <line
                x1={formaat.marge.left}
                x2={formaat.breedte - formaat.marge.right}
                y1={y(waarde)}
                y2={y(waarde)}
                stroke={isNullijn ? 'var(--c-text-tertiary)' : 'var(--c-border-soft)'}
                strokeWidth="1"
              />
              <text
                x={formaat.marge.left - 8}
                y={y(waarde) + 4}
                textAnchor="end"
                className={styles.axisLabel}
              >
                {formatteer(waarde)}
              </text>
            </g>
          );
        })}

        {labelIndexen.map((index) => (
          <text
            key={dagen[index]}
            x={x(index)}
            y={formaat.hoogte - 8}
            // Het eerste en laatste label uitlijnen op hun rand, anders vallen ze half buiten
            // het diagram.
            textAnchor={index === 0 ? 'start' : index === dagen.length - 1 ? 'end' : 'middle'}
            className={styles.axisLabel}
          >
            {formatDayLabel(dagen[index] as string)}
          </text>
        ))}

        {getekend.map((reeks) => {
          const reeksWaarden = getoondeWaarden(reeks);
          const laatste = laatsteWaarde(reeksWaarden);
          const { kleur, streep } = stijlVan(reeks);
          return (
            <g key={reeks.sourceId}>
              <path
                d={pad(reeksWaarden)}
                fill="none"
                stroke={kleur}
                strokeWidth={reeks.isOwn ? 3 : 1.8}
                strokeLinejoin="round"
                strokeLinecap="round"
                {...(streep ? { strokeDasharray: streep } : {})}
              />
              {formaat.eindpunt && laatste && (
                <circle cx={x(laatste.dag)} cy={y(laatste.waarde)} r="3.5" fill={kleur} />
              )}
            </g>
          );
        })}

        {/*
          De naamlabels als laatste, zodat ze over de lijnen heen komen te liggen in plaats van
          eronder te verdwijnen.
        */}
        {formaat.eindlabel &&
          eindlabels.map((label) => (
            <g key={label.sourceId}>
              {/*
                Een dun aanwijsstreepje zodra het label van zijn punt is weggeduwd; zonder dat is bij
                een stapel labels niet meer te zien welke bij welke lijn hoort.
              */}
              {Math.abs(label.y - label.puntY) > 3 && (
                <line
                  x1={label.puntX + 5}
                  y1={label.puntY}
                  x2={label.x - 3}
                  y2={label.y}
                  stroke={label.kleur}
                  strokeWidth="0.8"
                  opacity="0.5"
                />
              )}
              <text x={label.x} y={label.y + 3.5} className={styles.serieLabel} fill={label.kleur}>
                {label.tekst}
                <title>{label.volledig}</title>
              </text>
            </g>
          ))}
      </svg>

      <div className={styles.legend}>
        {series.map((reeks) => {
          const { kleur, streep } = stijlVan(reeks);
          const uit = hidden.has(reeks.sourceId);
          const basis = relatief && reeks.isOwn;
          const laatste = laatsteWaarde(getoondeWaarden(reeks));
          return (
            <button
              key={reeks.sourceId}
              type="button"
              className={uit ? styles.legendChipOff : styles.legendChip}
              onClick={() => onToggle(reeks.sourceId)}
              aria-pressed={!uit}
              // De eigen lijn is in procentweergave de nullijn; verbergen doet daar niets.
              disabled={basis}
            >
              {/*
                Het blokje is een stukje lijn en geen vierkantje, zodat een gestreepte bron ook in de
                legenda als gestreept te herkennen is — bij meer dan tien bronnen is dat het enige
                verschil met de bron die dezelfde kleur heeft.
              */}
              <svg className={styles.swatch} viewBox="0 0 16 4" aria-hidden="true">
                <line
                  x1="0"
                  y1="2"
                  x2="16"
                  y2="2"
                  stroke={basis ? 'var(--c-border)' : kleur}
                  strokeWidth="3"
                  {...(streep ? { strokeDasharray: '5 3' } : {})}
                />
              </svg>
              {reeks.isOwn ? '★ ' : ''}
              {reeks.name}
              {variant === 'volledig' && (
                <span className={styles.legendWaarde} style={{ color: uit ? undefined : kleur }}>
                  {basis ? 'basis' : laatste ? formatteer(laatste.waarde) : '—'}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
